import { Category } from './index';

/**
 * WhatsApp Business API pricing per message (in INR).
 * Based on Meta's INR rate card effective July 1, 2026
 * Source: developers.facebook.com/documentation/business-messaging/whatsapp/pricing ("INR rates" CSV)
 *
 * Every rate lives exactly once: explicitly priced markets in EXPLICIT_PRICING,
 * the regional rows in WHATSAPP_REGION_PRICING, and which countries a region
 * covers in REGION_COUNTRIES. WHATSAPP_PRICING (per ISO code) is derived.
 */

export type CategoryPricing = {
  [Category.MARKETING]: number;
  [Category.UTILITY]: number;
  [Category.AUTHENTICATION]: number;
};

// Partial pricing for custom overrides (not all categories required)
export type PartialCategoryPricing = Partial<CategoryPricing>;

// Custom pricing overrides by country ISO code
// Example: { "IN": { "MARKETING": 0.75 }, "US": { "MARKETING": 1.00, "UTILITY": 0.50 } }
export type CustomPricing = Record<string, PartialCategoryPricing>;

export type WhatsAppPricing = Record<string, CategoryPricing>;

/** Markets Meta prices individually (rate-card rows), ISO-2 → INR per message. */
export const EXPLICIT_PRICING: Record<string, CategoryPricing> = {
  // Argentina
  AR: { MARKETING: 4.5293, UTILITY: 1.9062, AUTHENTICATION: 1.9062 },
  // Brazil
  BR: { MARKETING: 4.5808, UTILITY: 0.4984, AUTHENTICATION: 0.4984 },
  // Chile
  CL: { MARKETING: 6.5135, UTILITY: 1.4654, AUTHENTICATION: 1.4654 },
  // Colombia
  CO: { MARKETING: 0.9161, UTILITY: 0.0588, AUTHENTICATION: 0.0588 },
  // Egypt
  EG: { MARKETING: 4.7191, UTILITY: 0.2639, AUTHENTICATION: 0.2639 },
  // France
  FR: { MARKETING: 6.299, UTILITY: 2.1994, AUTHENTICATION: 2.1994 },
  // Germany
  DE: { MARKETING: 10.0073, UTILITY: 4.0322, AUTHENTICATION: 4.0322 },
  // Hong Kong
  HK: { MARKETING: 5.3664, UTILITY: 1.9056, AUTHENTICATION: 1.9056 },
  // Hungary
  HU: { MARKETING: 6.2997, UTILITY: 2.5583, AUTHENTICATION: 2.5583 },
  // India
  IN: { MARKETING: 0.8631, UTILITY: 0.115, AUTHENTICATION: 0.115 },
  // Indonesia
  ID: { MARKETING: 3.0111, UTILITY: 1.8316, AUTHENTICATION: 1.8316 },
  // Israel
  IL: { MARKETING: 2.5871, UTILITY: 0.3885, AUTHENTICATION: 0.3885 },
  // Italy
  IT: { MARKETING: 5.8206, UTILITY: 2.1974, AUTHENTICATION: 2.1974 },
  // Malaysia
  MY: { MARKETING: 6.3037, UTILITY: 1.0262, AUTHENTICATION: 1.0262 },
  // Mexico
  MX: { MARKETING: 2.2357, UTILITY: 0.6226, AUTHENTICATION: 0.6226 },
  // Netherlands
  NL: { MARKETING: 11.7078, UTILITY: 3.6656, AUTHENTICATION: 3.6656 },
  // Nigeria
  NG: { MARKETING: 3.7851, UTILITY: 0.4915, AUTHENTICATION: 0.4915 },
  // Pakistan
  PK: { MARKETING: 3.4683, UTILITY: 0.7333, AUTHENTICATION: 0.7333 },
  // Peru
  PE: { MARKETING: 5.1536, UTILITY: 1.4662, AUTHENTICATION: 1.4662 },
  // Poland
  PL: { MARKETING: 2.6754, UTILITY: 0.8918, AUTHENTICATION: 0.8918 },
  // Qatar
  QA: { MARKETING: 2.4993, UTILITY: 0.8789, AUTHENTICATION: 0.8789 },
  // Romania
  RO: { MARKETING: 6.2997, UTILITY: 2.1197, AUTHENTICATION: 2.1197 },
  // Russia
  RU: { MARKETING: 5.8767, UTILITY: 2.9311, AUTHENTICATION: 2.9311 },
  // Saudi Arabia
  SA: { MARKETING: 3.6621, UTILITY: 0.7834, AUTHENTICATION: 0.7834 },
  // Singapore
  SG: { MARKETING: 5.3664, UTILITY: 1.1727, AUTHENTICATION: 1.1727 },
  // South Africa
  ZA: { MARKETING: 2.7821, UTILITY: 0.5579, AUTHENTICATION: 0.5579 },
  // Spain
  ES: { MARKETING: 5.1801, UTILITY: 1.4648, AUTHENTICATION: 1.4648 },
  // Turkey
  TR: { MARKETING: 0.7989, UTILITY: 0.066, AUTHENTICATION: 0.066 },
  // United Arab Emirates
  AE: { MARKETING: 3.6613, UTILITY: 1.1509, AUTHENTICATION: 1.1509 },
  // United Kingdom
  GB: { MARKETING: 4.6496, UTILITY: 1.6122, AUTHENTICATION: 1.6122 },
};

export const EXPLICIT_PRICING_COUNTRIES = Object.keys(EXPLICIT_PRICING);

/**
 * Meta's regional rate-card rows (INR per message). "Other" is the fallback
 * for countries not on the card at all.
 */
export const WHATSAPP_REGION_PRICING: Record<string, CategoryPricing> = {
  'North America': {
    MARKETING: 1.8313,
    UTILITY: 0.2491,
    AUTHENTICATION: 0.2491,
  },
  'Rest of Africa': {
    MARKETING: 1.6497,
    UTILITY: 0.293,
    AUTHENTICATION: 0.293,
  },
  'Rest of Asia Pacific': {
    MARKETING: 5.3664,
    UTILITY: 0.8282,
    AUTHENTICATION: 0.8282,
  },
  'Rest of Central & Eastern Europe': {
    MARKETING: 6.2997,
    UTILITY: 1.5496,
    AUTHENTICATION: 1.5496,
  },
  'Rest of Latin America': {
    MARKETING: 5.4207,
    UTILITY: 0.8278,
    AUTHENTICATION: 0.8278,
  },
  'Rest of Middle East': {
    MARKETING: 2.4993,
    UTILITY: 0.6665,
    AUTHENTICATION: 0.6665,
  },
  'Rest of Western Europe': {
    MARKETING: 4.3378,
    UTILITY: 1.2516,
    AUTHENTICATION: 1.2516,
  },
  Other: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
};

/** Countries billed at their region's row (ISO-2); explicit markets are not listed here. */
export const REGION_COUNTRIES: Record<string, string[]> = {
  'North America': [
    'CA', // Canada
    'US', // United States
  ],
  'Rest of Africa': [
    'DZ', // Algeria
    'AO', // Angola
    'BJ', // Benin
    'BW', // Botswana
    'BF', // Burkina Faso
    'BI', // Burundi
    'CM', // Cameroon
    'CV', // Cape Verde
    'CF', // Central African Republic
    'TD', // Chad
    'KM', // Comoros
    'CD', // Congo (DRC)
    'CG', // Congo (Republic)
    'CI', // Côte d'Ivoire
    'DJ', // Djibouti
    'GQ', // Equatorial Guinea
    'ER', // Eritrea
    'SZ', // Eswatini (Swaziland)
    'ET', // Ethiopia
    'GA', // Gabon
    'GM', // Gambia
    'GH', // Ghana
    'GN', // Guinea
    'GW', // Guinea-Bissau
    'KE', // Kenya
    'LS', // Lesotho
    'LR', // Liberia
    'LY', // Libya
    'MG', // Madagascar
    'MW', // Malawi
    'ML', // Mali
    'MR', // Mauritania
    'MU', // Mauritius
    'MA', // Morocco
    'MZ', // Mozambique
    'NA', // Namibia
    'NE', // Niger
    'RW', // Rwanda
    'ST', // São Tomé and Príncipe
    'SN', // Senegal
    'SC', // Seychelles
    'SL', // Sierra Leone
    'SO', // Somalia
    'SS', // South Sudan
    'SD', // Sudan
    'TZ', // Tanzania
    'TG', // Togo
    'TN', // Tunisia
    'UG', // Uganda
    'ZM', // Zambia
    'ZW', // Zimbabwe
  ],
  'Rest of Asia Pacific': [
    'AF', // Afghanistan
    'BD', // Bangladesh
    'BT', // Bhutan
    'BN', // Brunei
    'KH', // Cambodia
    'CN', // China
    'JP', // Japan
    'LA', // Laos
    'MO', // Macau
    'MV', // Maldives
    'MN', // Mongolia
    'MM', // Myanmar
    'NP', // Nepal
    'KP', // North Korea
    'PH', // Philippines
    'KR', // South Korea
    'LK', // Sri Lanka
    'TW', // Taiwan
    'TJ', // Tajikistan
    'TH', // Thailand
    'TL', // Timor-Leste
    'TM', // Turkmenistan
    'VN', // Vietnam
  ],
  'Rest of Central & Eastern Europe': [
    'AL', // Albania
    'AM', // Armenia
    'AZ', // Azerbaijan
    'BY', // Belarus
    'BA', // Bosnia and Herzegovina
    'BG', // Bulgaria
    'HR', // Croatia
    'CZ', // Czech Republic
    'EE', // Estonia
    'GE', // Georgia
    'KZ', // Kazakhstan
    'XK', // Kosovo
    'LV', // Latvia
    'LT', // Lithuania
    'MD', // Moldova
    'ME', // Montenegro
    'MK', // North Macedonia
    'RS', // Serbia
    'SK', // Slovakia
    'SI', // Slovenia
    'UA', // Ukraine
    'UZ', // Uzbekistan
  ],
  'Rest of Latin America': [
    'AG', // Antigua and Barbuda
    'BS', // Bahamas
    'BB', // Barbados
    'BZ', // Belize
    'BO', // Bolivia
    'CR', // Costa Rica
    'CU', // Cuba
    'DM', // Dominica
    'DO', // Dominican Republic
    'EC', // Ecuador
    'SV', // El Salvador
    'GD', // Grenada
    'GT', // Guatemala
    'GY', // Guyana
    'HT', // Haiti
    'HN', // Honduras
    'JM', // Jamaica
    'NI', // Nicaragua
    'PA', // Panama
    'PY', // Paraguay
    'PR', // Puerto Rico
    'KN', // Saint Kitts and Nevis
    'LC', // Saint Lucia
    'VC', // Saint Vincent and the Grenadines
    'SR', // Suriname
    'TT', // Trinidad and Tobago
    'UY', // Uruguay
    'VE', // Venezuela
  ],
  'Rest of Middle East': [
    'BH', // Bahrain
    'IR', // Iran
    'IQ', // Iraq
    'JO', // Jordan
    'KW', // Kuwait
    'LB', // Lebanon
    'OM', // Oman
    'PS', // Palestine
    'SY', // Syria
    'YE', // Yemen
  ],
  'Rest of Western Europe': [
    'AD', // Andorra
    'AT', // Austria
    'BE', // Belgium
    'CY', // Cyprus
    'DK', // Denmark
    'FI', // Finland
    'GR', // Greece
    'IS', // Iceland
    'IE', // Ireland
    'LI', // Liechtenstein
    'LU', // Luxembourg
    'MT', // Malta
    'MC', // Monaco
    'NO', // Norway
    'PT', // Portugal
    'SM', // San Marino
    'SE', // Sweden
    'CH', // Switzerland
    'VA', // Vatican City
  ],
  Other: [
    'AU', // Australia
    'FJ', // Fiji
    'KI', // Kiribati
    'MH', // Marshall Islands
    'FM', // Micronesia
    'NR', // Nauru
    'NZ', // New Zealand
    'PW', // Palau
    'PG', // Papua New Guinea
    'WS', // Samoa
    'SB', // Solomon Islands
    'TO', // Tonga
    'TV', // Tuvalu
    'VU', // Vanuatu
  ],
};

/**
 * Complete pricing by country ISO code (uppercase), INR per message: regional
 * rows expanded over their countries, explicit markets on top.
 */
export const WHATSAPP_PRICING: WhatsAppPricing = {
  ...Object.fromEntries(
    Object.entries(REGION_COUNTRIES).flatMap(([region, countries]) =>
      countries.map(iso => [iso, { ...WHATSAPP_REGION_PRICING[region] }]),
    ),
  ),
  ...EXPLICIT_PRICING,
};

/**
 * Authentication-international rate (INR per message) for the markets where
 * Meta publishes one; see pricing/authentication-international-rates.
 */
export const AUTH_INTERNATIONAL_PRICING: Record<string, number> = {
  EG: 4.7668,
  IN: 2.4971,
  ID: 9.9638,
  MY: 3.0639,
  NG: 5.5035,
  PK: 5.5035,
  SA: 4.3871,
  ZA: 1.4681,
  AE: 3.7342,
};

/**
 * Default pricing for unknown/unmapped countries
 * Uses "Other" region pricing
 */
export const DEFAULT_PRICING: CategoryPricing = WHATSAPP_REGION_PRICING.Other;

/**
 * Get pricing for a country by ISO code
 * Returns default pricing if country not found
 *
 * @param isoCode - Uppercase ISO 2-letter country code (e.g., 'IN', 'US')
 * @returns CategoryPricing object with MARKETING, UTILITY, AUTHENTICATION prices
 */
export const getPricingByCountry = (isoCode: string): CategoryPricing => {
  const upperIso = isoCode.toUpperCase();
  return WHATSAPP_PRICING[upperIso] ?? DEFAULT_PRICING;
};

/**
 * Get price for a specific country and category
 *
 * @param isoCode - Uppercase ISO 2-letter country code
 * @param category - Message category (MARKETING, UTILITY, AUTHENTICATION)
 * @returns Price per message in INR
 */
export const getPrice = (isoCode: string, category: Category): number => {
  const pricing = getPricingByCountry(isoCode);
  return pricing[category];
};

// ============================================
// RCS pricing
// ============================================

/**
 * Default flat RCS price per message (in INR), used as the final fallback when
 * neither a business-level RCS override nor an org-level RCS slab rate is set.
 *
 * RCS (v1) is billed as a single flat per-message rate with NO category and NO
 * per-country dimension — every RCS message is charged this rate (or the
 * configured business/org rate) regardless of recipient country. The pricing
 * config schema stays country-keyed (mirroring WhatsApp) so per-country RCS rates
 * can be added later without a data migration.
 */
export const RCS_DEFAULT_PRICE = 0.1;
